// Detects visitor country via edge function `geo-lookup`. Caches in sessionStorage for the session.
// Falls back to locale heuristic (fr→SN, pt→BR) if the edge function returns no country.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "bp.country";
const MANUAL_KEY = "bp.country.manual"; // user-set in checkout form, takes precedence

function localeHeuristic(lang: string): string | null {
  const l = (lang || "en").slice(0, 2).toLowerCase();
  if (l === "pt") return "BR";
  if (l === "fr") return "SN";
  return null;
}

export function useCountry(): { country: string | null; loading: boolean } {
  const { i18n } = useTranslation();
  const [country, setCountry] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return (
      window.localStorage.getItem(MANUAL_KEY) ||
      window.sessionStorage.getItem(SESSION_KEY) ||
      null
    );
  });
  const [loading, setLoading] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return !window.localStorage.getItem(MANUAL_KEY) && !window.sessionStorage.getItem(SESSION_KEY);
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(MANUAL_KEY)) return;
    if (window.sessionStorage.getItem(SESSION_KEY)) return;

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("geo-detect");
        if (cancelled) return;
        const c = (data as { country?: string | null } | null)?.country ?? null;
        if (error || !c) {
          const fallback = localeHeuristic(i18n.language);
          if (fallback) {
            window.sessionStorage.setItem(SESSION_KEY, fallback);
            setCountry(fallback);
          }
        } else {
          window.sessionStorage.setItem(SESSION_KEY, c);
          setCountry(c);
        }
      } catch {
        const fallback = localeHeuristic(i18n.language);
        if (!cancelled && fallback) {
          window.sessionStorage.setItem(SESSION_KEY, fallback);
          setCountry(fallback);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  return { country, loading };
}

/** Imperatively set the country (e.g. from a checkout form). Persists across sessions. */
export function setManualCountry(country: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(MANUAL_KEY, country.toUpperCase());
}
